import { useRef, useState } from 'react';
import { MAX_RIDE_NAME_LENGTH, normalizeRideName } from '../domain/ride-name';
import './ride-name.css';

interface RideNameProperties {
  title: string;
  onRename: (title: string) => boolean;
}

/** Edits the current ride's name without resetting playback or map settings. */
export function RideName({ title, onRename }: RideNameProperties) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const editButton = useRef<HTMLButtonElement>(null);
  const finish = () => {
    setEditing(false);
    requestAnimationFrame(() => editButton.current?.focus());
  };

  return <div className="ride-name">
    <h1>{editing ? title : <button ref={editButton} className="ride-name-title" type="button"
      aria-label={`Rename trace: ${title}`} title="Rename trace" onClick={() => {
      setDraft(title);
      setError('');
      setMessage('');
      setEditing(true);
    }}>{title}</button>}</h1>
    {editing && <form onSubmit={(event) => {
      event.preventDefault();
      try {
        const remembered = onRename(normalizeRideName(draft));
        setMessage(remembered ? 'Name saved in this browser.' : 'Name updated for this session. Browser storage is unavailable.');
        finish();
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'The name could not be saved.');
      }
    }} onKeyDown={(event) => {
      if (event.key === 'Escape') { event.preventDefault(); finish(); }
    }}>
      <label htmlFor="ride-name">Trace name</label>
      <input id="ride-name" autoFocus value={draft} maxLength={MAX_RIDE_NAME_LENGTH}
        aria-invalid={Boolean(error)} aria-describedby={error ? 'ride-name-error' : 'ride-name-help'}
        onFocus={(event) => event.currentTarget.select()}
        onChange={(event) => { setDraft(event.currentTarget.value); setError(''); }} />
      <p id="ride-name-help">Used for new video exports. Remembered in this browser.</p>
      {error && <p id="ride-name-error" role="alert">{error}</p>}
      <div className="ride-name-actions">
        <button className="primary-action" type="submit">Save name</button>
        <button className="secondary-action" type="button" onClick={finish}>Cancel</button>
      </div>
    </form>}
    <p className="ride-name-status" role="status">{message}</p>
  </div>;
}
